import React, { useContext, useMemo, useRef, useState, useLayoutEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Avatar, Box, IconButton, Tooltip } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import CommandActionSection from "../../external/ensolvers-core-frontend-mui/types/CommandActionSection";
import CommandAction from "../../external/ensolvers-core-frontend-mui/types/CommandAction";
import { UserInfoContext } from "../../context/UserInfoContext";
import { Token, useThemeMode } from "../../theme";
import { Routes } from "../../types/Routes";

interface Props {
  sections: CommandActionSection[];
  handleSignOut: () => void;
  showThemeToggle?: boolean;
}

const sectionForPath = (sections: CommandActionSection[], pathname: string) => {
  if (pathname.startsWith(Routes.PROFILE)) {
    return sections.find((s) => s.key === "me") ?? sections[0];
  }

  return sections.find((s) => s.items.some((i) => pathname.startsWith(i.actionPath))) ?? sections[0];
};

export const NavPill: React.FC<Props> = ({ sections, handleSignOut, showThemeToggle }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const info = useContext(UserInfoContext);
  const user = info?.opsUser ?? info?.user;

  const active = useMemo(() => sectionForPath(sections, pathname), [sections, pathname]);
  const [openKey, setOpenKey] = useState<string | undefined>(undefined);
  const shown = sections.find((s) => s.key === openKey) ?? active;

  const scroller = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sections]);

  const nudge = (dir: 1 | -1) => scroller.current?.scrollBy({ left: dir * 180, behavior: "smooth" });

  const go = (item: CommandAction) => history.push(item.actionPath);

  const initials = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((n: string) => n[0])
    .join("")
    .toUpperCase();

  return (
    <Box
      component="header"
      sx={{ position: "sticky", top: 0, zIndex: 1200, px: "var(--gutter)", pt: "var(--gutter)", backgroundColor: "var(--surface-app)" }}
    >
      <Box
        sx={{
          height: 54,
          borderRadius: "27px",
          backgroundColor: "var(--surface-card)",
          border: "1px solid var(--border-default)",
          boxShadow: "0 1px 3px rgba(14,19,26,.08), 0 1px 2px rgba(14,19,26,.04)",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: "10px",
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", pl: "6px", flexShrink: 0 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              backgroundColor: Token.brandAccent,
              transform: "rotate(45deg)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {/* Reads as a hole punched through the mark, so it follows the pill instead of a fixed colour. */}
            <Box sx={{ width: 8, height: 8, borderRadius: "1px", backgroundColor: "var(--surface-card)" }} />
          </Box>
          <Box sx={{ lineHeight: 1 }}>
            <Box sx={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>OPS</Box>
            <Box sx={{ fontSize: 7.5, letterSpacing: ".16em", color: "var(--text-tertiary)", fontWeight: 600 }}>ENSOLVERS</Box>
          </Box>
        </Box>

        {overflows && (
          <IconButton size="small" onClick={() => nudge(-1)} aria-label="Scroll sections left">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          ref={scroller}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            overflowX: "auto",
            flex: 1,
            minWidth: 0,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {sections.map((s) => {
            const isActive = s.key === shown?.key;
            return (
              <Box
                key={s.key}
                component="button"
                onClick={() => {
                  setOpenKey(s.key);
                  if (s.items[0]) go(s.items[0]);
                }}
                sx={{
                  height: 38,
                  borderRadius: "19px",
                  padding: "0 11px",
                  border: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  backgroundColor: isActive ? "var(--surface-selected)" : "transparent",
                  color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                  transition: "background-color 80ms var(--ease), color 80ms var(--ease)",
                  "&:hover": { backgroundColor: "var(--surface-hover)", color: "var(--text-primary)" },
                }}
              >
                {s.label}
              </Box>
            );
          })}
        </Box>

        {overflows && (
          <IconButton size="small" onClick={() => nudge(1)} aria-label="Scroll sections right">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          {showThemeToggle && (
            <Tooltip title={mode === "dark" ? "Switch to light" : "Switch to dark"}>
              <IconButton size="small" onClick={toggleMode} aria-label="Toggle theme">
                {mode === "dark" ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="My Profile">
            <Box
              component="button"
              onClick={() => {
                setOpenKey(undefined);
                history.push(Routes.PROFILE);
              }}
              aria-label="My Profile"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                height: 38,
                borderRadius: "19px",
                padding: "0 4px",
                border: "none",
                cursor: "pointer",
                backgroundColor: "transparent",
                fontFamily: "inherit",
                transition: "background-color 80ms var(--ease)",
                "&:hover": { backgroundColor: "var(--surface-hover)" },
              }}
            >
              <Avatar
                src={user?.avatarUrl}
                sx={{ width: 28, height: 28, fontSize: 11, backgroundColor: "var(--surface-sunken)", color: "var(--text-secondary)" }}
              >
                {initials}
              </Avatar>
              <Box sx={{ fontSize: 13, color: "var(--text-secondary)", whiteSpace: "nowrap", display: { xs: "none", lg: "block" } }}>
                {user?.firstName} {user?.lastName}
              </Box>
            </Box>
          </Tooltip>
          <Tooltip title="Log out">
            <IconButton size="small" onClick={handleSignOut} aria-label="Log out">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          height: Token.cardHeader,
          display: "flex",
          alignItems: "stretch",
          gap: "14px",
          px: Token.cardPad,
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {shown?.items.map((item) => {
          const isActive = pathname.startsWith(item.actionPath);
          return (
            <Box
              key={item.key}
              component="button"
              onClick={() => go(item)}
              title={item.tooltip}
              sx={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12.5,
                whiteSpace: "nowrap",
                padding: "0 2px",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                fontWeight: isActive ? 600 : 400,
                borderBottom: `2px solid ${isActive ? Token.brandAccent : "transparent"}`,
                transition: "color 80ms var(--ease)",
                "&:hover": { color: "var(--text-primary)" },
                "& .MuiSvgIcon-root": { fontSize: "1.25em" },
              }}
            >
              {item.icon && React.cloneElement(item.icon, { "aria-hidden": true })}
              {item.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
